import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Seo } from "../components/Seo";
import { Footer } from "../components/Footer";
import { api, formatApiErrorDetail } from "../lib/api";
import { MapPin, Trash2, ArrowRight, Compass, RotateCcw, Trash, Bell } from "lucide-react";

export default function SavedTrips() {
  const [trips, setTrips] = useState(null);
  const [trash, setTrash] = useState([]);
  const [showTrash, setShowTrash] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/trips");
      setTrips(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load trips");
      setTrips([]);
    }
  };

  const loadTrash = async () => {
    try {
      const { data } = await api.get("/trash/trips");
      setTrash(data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    loadTrash();
  }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/trips/${id}`);
      setTrips((t) => t.filter((x) => x.id !== id));
      loadTrash();
      toast.success("Trip moved to Trash — you can recover it");
    } catch (err) {
      toast.error("Could not delete");
    }
  };

  const recover = async (id) => {
    try {
      await api.post(`/trips/${id}/restore-trip`);
      setTrash((t) => t.filter((x) => x.id !== id));
      load();
      toast.success("Trip recovered");
    } catch {
      toast.error("Could not recover trip");
    }
  };

  const purge = async (id) => {
    try {
      await api.delete(`/trips/${id}/permanent`);
      setTrash((t) => t.filter((x) => x.id !== id));
      toast.success("Trip permanently deleted");
    } catch {
      toast.error("Could not delete trip");
    }
  };

  return (
    <div className="min-h-screen grain relative">
      <Seo title="My trips" description="All your saved WanderlyMe itineraries in one place — reopen, edit, share or recover from trash." path="/trips" noindex />
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight mb-2">My trips</h1>
        <p className="text-muted-foreground mb-6">Everything you've saved, ready to revisit.</p>

        {trash.length > 0 && (
          <div className="mb-8">
            <button
              data-testid="toggle-trash-btn"
              onClick={() => setShowTrash((s) => !s)}
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-[#C8553D] transition-colors"
            >
              <Trash className="w-4 h-4" /> Trash ({trash.length}) {showTrash ? "▲" : "▼"}
            </button>
            {showTrash && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="trash-list">
                {trash.map((t) => (
                  <div key={t.id} data-testid="trash-card" className="p-5 rounded-2xl border border-dashed border-black/15 bg-black/[0.02] flex flex-col">
                    <h3 className="font-display text-xl leading-tight">{t.name || t.destination}</h3>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground mt-2">{t.days} day itinerary · deleted</p>
                    <div className="mt-4 flex items-center gap-2">
                      <button
                        data-testid="trip-recover-btn"
                        onClick={() => recover(t.id)}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#1A3B2B] text-[#1A3B2B] text-sm hover:bg-[#1A3B2B] hover:text-[#FDFBF7] transition-colors"
                      >
                        <RotateCcw className="w-4 h-4" /> Recover
                      </button>
                      <button
                        data-testid="trip-purge-btn"
                        onClick={() => purge(t.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-muted-foreground hover:text-[#C8553D] text-sm transition-colors"
                      >
                        <Trash2 className="w-4 h-4" /> Delete forever
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {trips === null ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : trips.length === 0 ? (
          <div className="text-center py-24 rounded-3xl border border-dashed border-border" data-testid="trips-empty">
            <Compass className="w-10 h-10 text-[#C8553D] mx-auto mb-4" />
            <p className="font-display text-2xl mb-2">No trips yet</p>
            <p className="text-muted-foreground mb-6">Generate your first itinerary and save it here.</p>
            <Link
              to="/plan"
              className="inline-flex px-6 py-3 rounded-full bg-[#1A3B2B] text-[#FDFBF7] hover:bg-[#122a1f] transition-colors"
            >
              Plan a trip
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {trips.map((t, i) => (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                data-testid="trip-card"
                onClick={() => navigate(`/trips/${t.id}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && navigate(`/trips/${t.id}`)}
                className="group cursor-pointer p-6 rounded-2xl border border-black/5 bg-white/60 hover:-translate-y-1 hover:shadow-md transition-all flex flex-col"
              >
                <div className="flex items-start justify-between">
                  <MapPin className="w-6 h-6 text-[#C8553D] mb-4" />
                  <div className="flex items-center gap-2">
                    {t.collab_unread > 0 && (
                      <span
                        data-testid="trip-collab-unread"
                        title="New votes or comments from your travel friends"
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#C8553D] text-[#FDFBF7]"
                      >
                        <Bell className="w-3.5 h-3.5" /> {t.collab_unread} new
                      </span>
                    )}
                    <button
                      data-testid="trip-delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(t.id);
                      }}
                      className="text-muted-foreground hover:text-[#C8553D] transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <h3 className="font-display text-2xl leading-tight">{t.name || t.destination}</h3>
                {t.tagline && <p className="text-muted-foreground text-sm mt-2 line-clamp-2">{t.tagline}</p>}
                <p className="text-xs uppercase tracking-wide text-[#1A3B2B] mt-4">{t.days} day itinerary</p>
                <Link
                  to={`/trips/${t.id}`}
                  data-testid="trip-view-link"
                  onClick={(e) => e.stopPropagation()}
                  className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-[#1A3B2B] group-hover:text-[#C8553D] transition-colors"
                >
                  View plan <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
